import { useEffect, useRef, useState, useMemo } from "react";
import { useSelector } from "react-redux";
import { useAppDispatch } from "src/common/state/hooks";
import { useTranslation } from "react-i18next";
import Fuse from "fuse.js";
import {
  setMediaSearch,
  resetMediaSearch,
  fetchFilteredMedias,
  getMediaSearch,
} from "../state/gallerySlice";
import {
  DocumentIcon,
  PhotographIcon,
  CubeIcon,
  FilmIcon,
  MusicNoteIcon,
  UserIcon,
} from "@heroicons/react/outline";
import { MEDIA_LIST_COMPONENT_ID } from "../Gallery";

type MediaItem = {
  id: number | string;
  name: string;
  type: string;
  media_360_tag: string;
  updated_at?: string;
  organizationId?: number;
};

const getMediaIcon = (type: string) => {
  switch (type) {
    case "pdf":
      return <DocumentIcon className="h-5 w-5 text-gray-500" />;
    case "audio":
      return <MusicNoteIcon className="h-5 w-5 text-gray-500" />;
    case "video":
      return <FilmIcon className="h-5 w-5 text-gray-500" />;
    case "model3d":
      return <CubeIcon className="h-5 w-5 text-gray-500" />;
    case "avatar":
      return <UserIcon className="h-5 w-5 text-gray-500" />;
    default:
      return <PhotographIcon className="h-5 w-5 text-gray-500" />;
  }
};

const MediaSearchBar = ({
  className,
  allowedMediaTypeOverride,
  componentId,
}: {
  className?: string;
  allowedMediaTypeOverride?: string;
  componentId?: string;
}) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const searchIndex = useSelector((state: any) => state.media.searchIndex) as MediaItem[];
  const mediaSearch = useSelector(getMediaSearch);
  const { limit, sort } = mediaSearch;

  const [input, setInput] = useState("");
  const [filteredResults, setFilteredResults] = useState<MediaItem[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState<number>(-1);

  const wrapperRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const allowedTypes = useMemo<Set<string> | null>(() => {
    if (!allowedMediaTypeOverride) return null;
    return new Set(
      allowedMediaTypeOverride
        .split("|")
        .map((s) => s.trim().toLowerCase())
        .filter(Boolean),
    );
  }, [allowedMediaTypeOverride]);

  const fuseIndex = useMemo<MediaItem[]>(() => {
    if (!allowedTypes) return searchIndex;
    return searchIndex.filter((item) => allowedTypes.has((item.type || "").toLowerCase()));
  }, [searchIndex, allowedTypes]);

  useEffect(() => {
    if (highlightedIndex >= 0 && listRef.current) {
      const el = listRef.current.children[highlightedIndex] as HTMLElement;
      el?.scrollIntoView({ block: "nearest" });
    }
  }, [highlightedIndex]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setShowSuggestions(false);
        setHighlightedIndex(-1);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => {
    if (!input.trim()) {
      setFilteredResults([]);
      setShowSuggestions(false);
      return;
    }

    const fuse = new Fuse<MediaItem>(fuseIndex, {
      keys: ["name"],
      threshold: 0.3,
      includeScore: true,
      ignoreLocation: true,
    });

    const results = fuse.search(input.trim());
    setFilteredResults(results.slice(0, 20).map((r) => r.item));
    setShowSuggestions(true);
    setHighlightedIndex(-1);
  }, [input, fuseIndex]);

  useEffect(() => {
    setInput("");
  }, [mediaSearch.owner, mediaSearch.sort, mediaSearch.media360tag, mediaSearch.mediaType]);

  const handleSearchBarSelect = (item: MediaItem) => {
    const defaultSearchValues = {
      page: 0,
      mediaType: allowedTypes ? item.type : "all",
      owner: "all",
      media360tag: "",
    };
    dispatch(setMediaSearch({ ...defaultSearchValues, selectedId: item.id }));
    setInput(item.name);
    setHighlightedIndex(-1);

    requestAnimationFrame(() => {
      setShowSuggestions(false);
    });

    dispatch(
      fetchFilteredMedias({
        ...defaultSearchValues,
        limit,
        sort,
        selectedId: Number(item.id),
        componentId: componentId ? componentId : MEDIA_LIST_COMPONENT_ID,
      }),
    );
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    console.log("keydown", e.key, "native defaultPrevented =", e.nativeEvent.defaultPrevented);
    if (!showSuggestions || filteredResults.length === 0) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightedIndex((prev) => Math.min(prev + 1, filteredResults.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightedIndex((prev) => Math.max(prev - 1, 0));
    } else if (e.key === "Enter" && highlightedIndex >= 0) {
      e.preventDefault();
      handleSearchBarSelect(filteredResults[highlightedIndex]);
    } else if (e.key === "Escape") {
      setShowSuggestions(false);
      setHighlightedIndex(-1);
    }
  };

  return (
    <div className={`search-filter flex items-end w-[15rem] ${className}`}>
      <div ref={wrapperRef} className="relative w-full">
        <input
          type="text"
          className="w-full border border-gray-300 rounded-[10px] px-3 py-2 text-sm h-[46px] pr-8 focus:outline-none focus:ring-1 focus:ring-primary"
          placeholder={`${t("general.search")}...`}
          value={input}
          onChange={(e) => {
            setInput(e.target.value);
          }}
          onFocus={() => {
            if (filteredResults.length > 0) setShowSuggestions(true);
          }}
          onKeyDown={handleKeyDown}
          onInput={(e) => console.log("onInput defaultPrevented=", e.nativeEvent.defaultPrevented)}
        />
        {input && (
          <button
            onClick={() => {
              setInput("");
              setFilteredResults([]);
              setShowSuggestions(false);
              if (!allowedMediaTypeOverride) {
                dispatch(resetMediaSearch());
              }
            }}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 text-lg"
            aria-label="Clear"
          >
            ×
          </button>
        )}

        {showSuggestions && (
          <div
            ref={listRef}
            className="absolute top-full left-0 w-full bg-white border border-gray-300 rounded-[10px] mt-[1px] z-20 shadow-md max-h-64 overflow-y-auto overflow-x-hidden"
          >
            {filteredResults.length > 0 ? (
              filteredResults.map((item, idx) => {
                const isActive = idx === highlightedIndex;
                return (
                  <button
                    key={item.id}
                    onClick={() => handleSearchBarSelect(item)}
                    className={`flex items-start w-full text-left px-3 py-2 text-sm ${
                      isActive ? "bg-gray-100" : "hover:bg-gray-50"
                    }`}
                  >
                    <span className="mt-[2px] shrink-0">{getMediaIcon(item.type)}</span>
                    <div className="ml-2 truncate">
                      <div className="font-medium text-sm text-gray-800 truncate">{item.name}</div>
                    </div>
                  </button>
                );
              })
            ) : (
              <div className="text-sm text-gray-500 px-4 py-3 italic">{t("general.noResults")}</div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default MediaSearchBar;
